import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Assert } from '@cotera/utilities';
import { cn } from '@cotera/client/v0/lib/utils';
import { NODE_ACCENTS, type NodeAccent } from './node-accent';
import {
  WORKFLOW_NODE_PALETTE_SECTIONS,
  type WorkflowNodePaletteBuiltinItem,
  type WorkflowNodePaletteSection,
} from '../workflow-node-palette-registry';

export type WorkflowNodePaletteSelection =
  | { kind: 'agent'; agentId: string; agentName: string }
  | { kind: 'tool-call'; toolKey: string; toolName: string }
  | {
      kind: 'run-workflow';
      targetWorkflowId: string;
      workflowName: string;
    }
  | { kind: 'for-each' }
  | { kind: 'filter' }
  | { kind: 'debounce' }
  | { kind: 'mapper' }
  | { kind: 'emit-event' }
  | { kind: 'bucket' }
  | { kind: 'group-by' };

/** The builtin flow-control node types, flattened out of the section registry. */
export const FLOW_CONTROL_ITEMS: readonly WorkflowNodePaletteBuiltinItem[] =
  WORKFLOW_NODE_PALETTE_SECTIONS.flatMap(
    (section: WorkflowNodePaletteSection) =>
      'items' in section ? section.items : []
  );

export const flowControlSelection = (
  item: WorkflowNodePaletteBuiltinItem
): WorkflowNodePaletteSelection => {
  switch (item.kind) {
    case 'for-each':
      return { kind: 'for-each' };
    case 'filter':
      return { kind: 'filter' };
    case 'debounce':
      return { kind: 'debounce' };
    case 'mapper':
      return { kind: 'mapper' };
    case 'emit-event':
      return { kind: 'emit-event' };
    case 'bucket':
      return { kind: 'bucket' };
    case 'group-by':
      return { kind: 'group-by' };
    default:
      return Assert.unreachable(item.kind);
  }
};

/** Shared look for every clickable card/row in the new-trigger wizard. */
export const PALETTE_CARD_CLASS =
  'group flex gap-4 rounded-lg border border-border/60 bg-card text-left transition-all hover:border-primary/30 hover:bg-accent/30 hover:shadow-sm';

/** Shared 40px icon chip (compose with an accent or neutral background). */
export const PALETTE_CHIP_CLASS =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-md [&_svg]:h-5 [&_svg]:w-5';

/**
 * Card matching the tools modal's ToolCard look, for node families that don't
 * carry a provider brand icon (agents, flow control, trigger). Accent icon chip
 * + title + a two-line description.
 */
export const PaletteCard: React.FC<{
  icon: LucideIcon;
  accent: NodeAccent;
  title: string;
  description: string;
  onSelect: () => void;
}> = ({ icon: Icon, accent, title, description, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(PALETTE_CARD_CLASS, 'items-start px-5 py-3')}
  >
    <span className={cn(PALETTE_CHIP_CLASS, NODE_ACCENTS[accent].chip)}>
      <Icon />
    </span>
    <span className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
      <span className="truncate text-sm font-medium transition-colors group-hover:text-primary">
        {title}
      </span>
      <span className="line-clamp-2 text-xs text-muted-foreground">
        {description}
      </span>
    </span>
  </button>
);
